import {
  createContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { applyTheme } from "@/shared/theme/applyTheme";
import {
  DARK_SCHEME_QUERY,
  readThemePreference,
  resolveTheme,
  storeThemePreference,
  systemPrefersDark,
  type Theme,
  type ThemePreference,
} from "@/shared/theme/theme";

interface ThemeContextValue {
  /** What the person chose: follow the system, or always light or dark. */
  preference: ThemePreference;
  /** What the page shows now. */
  theme: Theme;
  setPreference: (preference: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function subscribeToSystemScheme(onChange: () => void): () => void {
  if (typeof window.matchMedia !== "function") return () => undefined;
  const query = window.matchMedia(DARK_SCHEME_QUERY);
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}

/**
 * The colour theme for the whole app. It starts from what index.html's boot script already
 * applied, follows the system while the preference is "system", and remembers the choice.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(() =>
    readThemePreference(localStorage),
  );
  const systemDark = useSyncExternalStore(subscribeToSystemScheme, systemPrefersDark);
  const theme = resolveTheme(preference, systemDark);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    storeThemePreference(localStorage, preference);
  }, [preference]);

  const value = useMemo(() => ({ preference, theme, setPreference }), [preference, theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
